import * as React from 'react';
import { Typography, Button } from 'components/common';
import AppRoutes from '@/const/routes';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';
import { LayoutLogin } from '@/components/layout';
import { verificationsService } from '@/api';
import { useRouter } from 'next/router';
import CustomInputOtp from '@/components/common/custom-input-otp/CustomInputOtp';
import { CircleAlert } from 'lucide-react';
import { useSession } from 'next-auth/react';
import clsx from 'clsx';

export const VerifyPhoneScreen: React.FC = (): JSX.Element => {
	const router = useRouter();
	const { data: session, update } = useSession();
	const { toastSuccess, toastError } = useNotification();
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string>('');
	const [code, setCode] = React.useState<string>('');
	const [phone, setPhone] = React.useState<string>('');

	React.useEffect(() => {
		if (session) {
			const lastNumber = session.user.user?.phonenumber as string;
			setPhone(lastNumber?.slice(-2)); // last two digits
		}
	}, [session]);

	const handleChangeCode = (code: string): void => {
		setCode(code);
		setError('');
	};

	const handleVerifyCode = async (): Promise<void> => {
		setIsLoading(true);
		try {
			await verificationsService.validatePhoneOtp(code, '2fa');
			await update({
				...session,
				user: {
					...session?.user,
					is_partial_login: false,
				},
			});
			router.push(AppRoutes.CLIENTAPP_DASHBOARD);
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsLoading(false);
		}
	};

	const handleSendCode = async (): Promise<void> => {
		try {
			await verificationsService.verifyNumber('2fa');
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	const handleSendCodeAgain = (): void => {
		handleSendCode();
		toastSuccess('Verification code has been successfully sent.');
	};

	return (
		<LayoutLogin
			customTitle={
				<>
					Verify Your <span>Phone</span>
				</>
			}
			buttonBack={true}
			rol="lawyer"
		>
			<div className="flex flex-col items-center justify-between w-full">
				<Typography
					type="regular-18"
					className="text-opacity-black text-center mb-10"
				>
					{`We've sent a code to your phone number ending in XXXXXX${phone}.`}
				</Typography>
				<CustomInputOtp
					length={4}
					onChange={handleChangeCode}
					classNameSlot={clsx('bg-white border-opacity-primary', {
						'ring-1 ring-red-600': error,
					})}
				/>
				{error && (
					<div className="flex items-center gap-2">
						<CircleAlert className="text-red-600 !h-5 !w-5" />
						<Typography type="regular-12" className="text-red-600">
							{error}
						</Typography>
					</div>
				)}
				<Typography type="regular-16" className="mt-4">
					{`Didn't receive the code? `}
					<button
						type="button"
						className="text-primary font-semibold"
						onClick={handleSendCodeAgain}
					>
						Resend verification code
					</button>
				</Typography>
				<Button
					dataCY="signin"
					size="full"
					decoration="primary"
					label="Sign In"
					loading={isLoading}
					disabled={isLoading || !code || code.length < 4}
					className="mt-8"
					onClick={handleVerifyCode}
				/>
			</div>
		</LayoutLogin>
	);
};
